/** Data hooks shared by the network pages. All server access goes through the shim `api`, all engine access through the shim hooks. */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ContactInput, Id, MatchActionRequest, MatchActionResponse, MatchCard, Need, Service } from "@hub/shared";
import { api, invokeEngine, useEngineQuery, useSession } from "../_shims";

export const MATCHES_KEY = ["network", "matches"] as const;

const HUB_LOOKUP_TYPES = new Set(["email", "phone", "linkedin"]);

/**
 * The Hub user a person is: the contact's own link (your own card), otherwise whoever the server finds by the
 * person's emails, phones and LinkedIn. The server keeps those links to itself, so the contact row alone only knows you.
 */
export function useHubUserOf(contactId: string): Id | null {
  const { isSignedIn } = useSession();
  const detail = useEngineQuery("getContact", { contactId });
  const own = detail.data?.contact.linkedUserId ?? null;
  const lookups = (detail.data?.lookups ?? []).filter((l) => HUB_LOOKUP_TYPES.has(l.lookup.type)).map((l) => ({ type: l.lookup.type as "email" | "phone" | "linkedin", value: l.lookup.value }));
  const key = lookups.map((l) => `${l.type}:${l.value}`).sort().join(",");
  const found = useQuery({
    queryKey: ["network", "hub-user", contactId, key],
    queryFn: () => api.linkContacts({ lookups }),
    enabled: isSignedIn && !own && lookups.length > 0,
    staleTime: 60_000,
  });
  return own ?? found.data?.links[0]?.userId ?? null;
}

/**
 * Introductions: when signed in, fetch from the server and cache the cards in the engine; when signed out or the server is
 * unreachable, read the cached cards so the page still works offline.
 */
export function useMatchCards() {
  const { isSignedIn } = useSession();
  return useQuery<{ cards: MatchCard[]; source: "server" | "cache" }>({
    queryKey: [...MATCHES_KEY, isSignedIn],
    queryFn: async () => {
      if (!isSignedIn) return { cards: await invokeEngine("getMatchCards", {}), source: "cache" };
      try {
        const res = await api.matches();
        await invokeEngine("cacheMatches", { cards: res.cards });
        return { cards: res.cards, source: "server" };
      } catch {
        return { cards: await invokeEngine("getMatchCards", {}), source: "cache" };
      }
    },
    staleTime: 30_000,
  });
}

export function useMatchCard(matchId: string | undefined) {
  const list = useMatchCards();
  const fromList = list.data?.cards.find((c) => c.matchId === matchId) ?? null;
  const cached = useEngineQuery("getMatchCard", { matchId: matchId ?? "" }, { enabled: Boolean(matchId) && !fromList && list.isFetched });
  return { card: fromList ?? cached.data ?? null, isLoading: list.isLoading || (!fromList && cached.isLoading), refetch: list.refetch };
}

export function useRefreshMatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.discover(),
    onSettled: () => qc.invalidateQueries({ queryKey: MATCHES_KEY }),
  });
}

export function useMatchAction(matchId: string) {
  const qc = useQueryClient();
  return useMutation<MatchActionResponse, Error, MatchActionRequest>({
    mutationFn: (body) => api.matchAction(matchId, body),
    onSuccess: async (res) => {
      await invokeEngine("cacheMatches", { cards: [res.card] });
      qc.invalidateQueries({ queryKey: MATCHES_KEY });
      qc.invalidateQueries({ queryKey: ["engine", "getMatchCards"] });
      qc.invalidateQueries({ queryKey: ["engine", "getMatchCard"] });
      qc.invalidateQueries({ queryKey: ["network", "credits"] });
    },
  });
}

// Self contact -------------------------------------------------------------
export function useSelfContact() {
  return useEngineQuery("getSelfContact", undefined);
}

/** Returns the self contact id, creating the self contact from the session user when the workspace has none yet. */
export function useEnsureSelfContact() {
  const { user } = useSession();
  const qc = useQueryClient();
  return useCallback(async (): Promise<Id> => {
    const existing = await invokeEngine("getSelfContact", undefined);
    if (existing) return existing.contact.id;
    const input: ContactInput = {
      firstName: user?.firstName ?? null,
      lastName: user?.lastName ?? null,
      displayName: user ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || user.email : "Me",
      emails: user?.email ? [{ value: user.email }] : [],
      source: "hub",
    };
    const created = await invokeEngine("createContact", input);
    await invokeEngine("setSelfContact", { contactId: created.contactId });
    qc.invalidateQueries({ queryKey: ["engine"] });
    return created.contactId;
  }, [user, qc]);
}

// Needs and services -------------------------------------------------------
export type SaveNeedInput = Partial<Need> & { contactId: Id; title: string };
export type SaveServiceInput = Partial<Service> & { contactId: Id; title: string };

/** Saves locally, then mirrors to the server when signed in. The server copy is what matching runs on. */
export function useSaveNeed() {
  const { isSignedIn } = useSession();
  const qc = useQueryClient();
  return useMutation<Need, Error, SaveNeedInput>({
    mutationFn: async (input) => {
      const need = await invokeEngine("saveNeed", input);
      if (isSignedIn) await api.putNeeds({ needs: [need] }).catch(() => undefined);
      return need;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["engine"] }),
  });
}

export function useSaveService() {
  const { isSignedIn } = useSession();
  const qc = useQueryClient();
  return useMutation<Service, Error, SaveServiceInput>({
    mutationFn: async (input) => {
      const service = await invokeEngine("saveService", input);
      if (isSignedIn) await api.putServices({ services: [service] }).catch(() => undefined);
      return service;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["engine"] }),
  });
}

export function useCompleteNeed() {
  const { isSignedIn } = useSession();
  const qc = useQueryClient();
  return useMutation<Need, Error, Parameters<typeof invokeEngine<"completeNeed">>[1]>({
    mutationFn: async (params) => {
      const need = await invokeEngine("completeNeed", params);
      if (isSignedIn) await api.putNeeds({ needs: [need] }).catch(() => undefined);
      return need;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["engine"] }),
  });
}

export function useDeleteItem(kind: "need" | "service") {
  const qc = useQueryClient();
  return useMutation<void, Error, Id>({
    mutationFn: (id) => (kind === "need" ? invokeEngine("deleteNeed", { needId: id }) : invokeEngine("deleteService", { serviceId: id })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["engine"] }),
  });
}

// Server collections -------------------------------------------------------
export function useConnections(enabled = true) {
  const { isSignedIn } = useSession();
  return useQuery({ queryKey: ["network", "connections"], queryFn: () => api.connections(), enabled: enabled && isSignedIn, staleTime: 30_000 });
}

export function useCredits() {
  const { isSignedIn } = useSession();
  return useQuery({ queryKey: ["network", "credits"], queryFn: () => api.credits(), enabled: isSignedIn, staleTime: 30_000 });
}

export function useConversations(poll = false) {
  const { isSignedIn } = useSession();
  return useQuery({
    queryKey: ["network", "conversations"],
    queryFn: () => api.conversations(),
    enabled: isSignedIn,
    refetchInterval: poll ? () => (typeof document !== "undefined" && document.visibilityState === "visible" ? 15_000 : false) : false,
  });
}

// Contact shares -----------------------------------------------------------
export const SHARES_KEY = ["network", "shares"] as const;

/** Every share either way: what was shared with me and what I shared. Polls while the page is visible so a new share badges the nav. */
export function useShares(enabled = true, poll = false) {
  const { isSignedIn } = useSession();
  return useQuery({
    queryKey: SHARES_KEY,
    queryFn: () => api.shares(),
    enabled: enabled && isSignedIn,
    staleTime: 30_000,
    refetchInterval: poll ? () => (typeof document !== "undefined" && document.visibilityState === "visible" ? 60_000 : false) : false,
  });
}

/** The shares I started for one of my people, newest first: the person page shows them as the pending state. */
export function useContactShares(contactId: Id | null) {
  const { user } = useSession();
  const shares = useShares(Boolean(contactId));
  const mine = (shares.data?.shares ?? []).filter((sh) => sh.contactId === contactId && sh.fromUserId === user?.id);
  return { shares: mine, isLoading: shares.isLoading };
}

/** Share a person with a connection. The server copies only the contact payload (name, how to reach them, where they work). */
export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toUserId: Id; contactId: Id; message: string | null }) => api.createShare({ toUserId: input.toUserId, contactIds: [input.contactId], message: input.message, contactPayloads: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SHARES_KEY }),
  });
}

/** Directory search for the share sheet: people on Hub whose name or needs match, from two characters on. */
export function useDirectorySearch(q: string, enabled = true) {
  const { isSignedIn } = useSession();
  const trimmed = q.trim();
  return useQuery({
    queryKey: ["network", "directory", trimmed],
    queryFn: () => api.directory({ q: trimmed, limit: 20 }),
    enabled: enabled && isSignedIn && trimmed.length >= 2,
    staleTime: 30_000,
  });
}
